import api from 'services/api';
import { array, object, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

import { flowDefinitionEdgeSchema, flowDefinitionNodeSchema, type FlowDefinitionEdge, type FlowDefinitionNode } from './types';

// A PROJECTION CONTRACT over `GET /node_definitions/:id` — see ../../README.md. The full contract for this route is
// `getNodeDefinition`; this one declares only the Flow graph, for the canvases that SHOW another definition's graph: Flow's
// subflow navigation and NodeDefinitionEditor's inline expand. Neither edits what it shows.
//
// The rows are the shared `FlowDefinitionNode` / `FlowDefinitionEdge` (./types), and each declares every key the backend's
// serializer sends: both consumers spread a row onto a canvas node, and `object()` drops what a schema does not declare.

/** A definition's Flow graph. Empty for a definition that is not a `flow`. */
export interface NodeDefinitionGraph {
  readonly child_nodes: readonly FlowDefinitionNode[];
  readonly child_edges: readonly FlowDefinitionEdge[];
}

const graphSchema = object<NodeDefinitionGraph>({
  child_nodes: array(flowDefinitionNodeSchema),
  child_edges: array(flowDefinitionEdgeSchema),
});

/**
 * The Flow graph of definition `id`. A missing definition is not a 404 here: the backend answers it with a 500, so it
 * rejects like any other failed call.
 */
export const getNodeDefinitionGraph = async (id: number): Promise<Received<NodeDefinitionGraph>> => {
  const res = await api.get(`/node_definitions/${id}`);
  return readResponse(res.data, { schema: graphSchema, source: 'getNodeDefinitionGraph' });
};
